// Timeline view component showing recent events
import React, { useEffect, useState, useRef } from 'react';
import { healthAPI } from '../api';
import { HealthEvent } from '../types';
import { EventCard } from './EventCard';
import './Timeline.css';

interface TimelineProps {
  refreshTrigger?: number;
  highlightTraceId?: string | null;
}

export const Timeline: React.FC<TimelineProps> = ({ refreshTrigger, highlightTraceId }) => {
  const [events, setEvents] = useState<HealthEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [filter, setFilter] = useState<'all' | 'bathroom' | 'meal' | 'weight' | 'blood_pressure' | 'water'>('all');
  const [units, setUnits] = useState<{ weight: 'kg' | 'lbs'; water: 'ml' | 'oz' }>({ weight: 'kg', water: 'ml' });

  // Polling ref to clear interval on unmount
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const loadData = async (background = false) => {
    if (!background) setLoading(true);
    setError('');

    try {
      const [eventsResponse, profile] = await Promise.all([
        healthAPI.getEvents({
          limit: 50,
          type: filter !== 'all' ? filter : undefined,
          exclude_types: filter === 'all' ? ['bug', 'idea'] : undefined
        }),
        healthAPI.getUserProfile().catch(() => ({ settings: { units: { weight: 'kg', water: 'ml' } } } as any))
      ]);

      setEvents(eventsResponse.events);
      if (profile.settings?.units) {
        setUnits({
          weight: (profile.settings.units.weight as 'kg' | 'lbs') || 'kg',
          water: (profile.settings.units.water as 'ml' | 'oz') || 'ml'
        });
      }

      // Handle highlighting
      if (highlightTraceId && !background) {
        scrollToEvent(highlightTraceId);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      console.error('Error loading data:', err);

      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        setError('Please sign in to view events');
      } else {
        setError(errorMessage);
      }
    } finally {
      if (!background) setLoading(false);
    }
  };

  const scrollToEvent = (traceId: string) => {
    // Give time for render
    setTimeout(() => {
      const el = document.getElementById(`event-${traceId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  };

  // Poll for updates if highlighted event is incomplete
  useEffect(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    if (highlightTraceId) {
      const event = events.find(e => e.trace_id === highlightTraceId);
      // If event exists but missing AI analysis (for meal/bathroom), poll
      const isAnalyzable = event && (event.type === 'meal' || event.type === 'bathroom');
      const isIncomplete = isAnalyzable && !event.ai_observation;

      if (isIncomplete) {
        // Poll every 3 seconds
        pollIntervalRef.current = setInterval(() => {
          loadData(true); // background update
        }, 3000);
      }
    }

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [highlightTraceId, events]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, refreshTrigger, highlightTraceId]);

  const handleDelete = async (traceId: string) => {
    try {
      await healthAPI.deleteEvent(traceId);
      setEvents(events.filter(e => e.trace_id !== traceId));
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event');
    }
  };

  const handleUpdate = () => {
    loadData(true); // Refresh data in background
  };

  if (loading && events.length === 0) {
    return (
      <div className="timeline-container">
        <div className="loading">Loading events...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="timeline-container">
        <div className="error">{error}</div>
        <button onClick={() => loadData()} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="timeline-container">
      <div className="timeline-header">
        <h2>Recent Events</h2>
        <div className="filter-buttons">
          {/* Filter buttons logic remains same, abstracted for brevity in this replace */}
          {(['all', 'bathroom', 'meal', 'weight', 'blood_pressure', 'water'] as const).map(f => (
            <button
              key={f}
              className={filter === f ? 'active' : ''}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'bathroom' ? '🚽 Bathroom' : f === 'meal' ? '🍽️ Meals' : f === 'weight' ? '⚖️ Weight' : f === 'blood_pressure' ? '🩺 BP' : '💧 Water'}
            </button>
          ))}
        </div>
      </div>

      <div className="timeline-events">
        {events.length === 0 ? (
          <div className="no-events">No events found</div>
        ) : (
          events.map(event => (
            <EventCard
              key={event.trace_id}
              event={event}
              units={units}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
              isHighlighted={event.trace_id === highlightTraceId}
            />
          ))
        )}
      </div>
    </div>
  );
};
